import React, { useLayoutEffect, useRef, useState } from "react";
import "../../style.css";

const DEFAULT_BASE_W = 1440;
const DEFAULT_BASE_H = 900;

/**
 * Live URL in an iframe; optional `openHref` adds a full-area link on top.
 * `scaledFit` + `scaledFitMode`:
 * - "fillWidth": scale to container width (may clip vertically).
 * - "contain": scale so the full virtual viewport (base W×H) fits inside the
 *   box—nothing cut off; may letterbox slightly.
 */
function ProjectEmbed({
  src,
  title = "",
  className = "",
  openHref,
  linkLabel = "Open site",
  scaledFit = false,
  scaledFitMode = "fillWidth",
  scaledBaseWidth = DEFAULT_BASE_W,
  scaledBaseHeight = DEFAULT_BASE_H,
}) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.35);

  useLayoutEffect(() => {
    if (!scaledFit || !wrapRef.current) return;

    const el = wrapRef.current;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w <= 0 || h <= 0) return;
      let s;
      if (scaledFitMode === "contain") {
        s = Math.min(w / scaledBaseWidth, h / scaledBaseHeight);
      } else {
        s = w / scaledBaseWidth;
      }
      setScale(s);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scaledFit, scaledFitMode, scaledBaseWidth, scaledBaseHeight]);

  if (scaledFit) {
    const viewportContain =
      scaledFitMode === "contain" ? " project-embed-scaled-viewport--contain" : "";
    return (
      <div className={`project-embed project-embed--scaled ${className}`.trim()}>
        <div
          ref={wrapRef}
          className={`project-embed-scaled-viewport${viewportContain}`.trim()}
        >
          <div
            className="project-embed-scaled-frame"
            style={{
              width: scaledBaseWidth * scale,
              height: scaledBaseHeight * scale,
            }}
          >
            <iframe
              title={title || undefined}
              aria-hidden={openHref ? "true" : undefined}
              src={src}
              loading="lazy"
              className="project-embed-iframe project-embed-iframe--scaled"
              referrerPolicy="no-referrer-when-downgrade"
              style={{
                width: scaledBaseWidth,
                height: scaledBaseHeight,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
            />
          </div>
        </div>
        {openHref ? (
          <a
            className="project-embed-click-overlay"
            href={openHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="visually-hidden">{linkLabel}</span>
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`project-embed ${className}`.trim()}>
      <iframe
        title={title || undefined}
        aria-hidden={openHref ? "true" : undefined}
        src={src}
        loading="lazy"
        className="project-embed-iframe"
        referrerPolicy="no-referrer-when-downgrade"
      />
      {openHref ? (
        <a
          className="project-embed-click-overlay"
          href={openHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="visually-hidden">{linkLabel}</span>
        </a>
      ) : null}
    </div>
  );
}

export default ProjectEmbed;
